import { Box, Button, Grid } from '@mui/material';
import React, {
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type MailViewerProps = {
  html: string;
};

export const MailViewer: React.FC<MailViewerProps> = ({ html }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(0);
  const [collapsedHeight, setCollapsedHeight] = useState(0);

  const maxCollapsed = 250; // maks høyde i "Se mer"-modus

  // 1) Grov deteksjon: ser dette ut som HTML?
  const isProbablyHtml = useMemo(() => {
    if (!html) return false;
    // enkel sjekk: finnes det noen vanlige HTML-tagger?
    const hasTag = /<\/?[a-z][\s\S]*>/i.test(html);
    return hasTag;
  }, [html]);

  // 2) Forbered innholdet som faktisk skal inn i <body>...</body>
  const bodyContent = useMemo(() => {
    if (!html) return '';

    if (isProbablyHtml) {
      // La HTML være i fred
      return html;
    }

    // Behandle som ren tekst:
    //  a) escape HTML-tegn
    let escaped = html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    //  b) konverter linjeskift til <br />
    escaped = escaped.replace(/\r\n|\n|\r/g, '<br />');

    return escaped;
  }, [html, isProbablyHtml]);

  // 3) Wrap alt i et lite HTML-dokument med styling
  const wrappedHtml = useMemo(
    () => `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: transparent;
        color: #e0e0e0;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        overflow: hidden;
        font-size: 14px;
        line-height: 1.4;
      }
      * { scrollbar-width: none; }
      *::-webkit-scrollbar { display: none; }
      a { color: #90caf9; }
    </style>
  </head>
  <body>${bodyContent}</body>
</html>`,
    [bodyContent]
  );

  // 4) Auto-høyde på iframe
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const updateHeight = () => {
      try {
        const full = iframe.contentDocument?.body?.scrollHeight ?? 0;

        setIframeHeight(full);
        setCollapsedHeight(Math.min(full, maxCollapsed));
      } catch {
        // ignorer cross-origin-feil osv.
      }
    };

    iframe.onload = updateHeight;

    // ekstra forsøk i tilfelle innholdet bruker bilder / fonts som loader senere
    setTimeout(updateHeight, 150);
    setTimeout(updateHeight, 500);
  }, [wrappedHtml]);

  const visibleHeight = expanded ? iframeHeight : collapsedHeight;

  return (
    <Box
      sx={{ 
        width: '100%',
       }}
      // Viktig: ikke la klikk inne i mailen trigge parent onClick (select post)
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          height: visibleHeight,
          transition: 'height 0.3s ease',
        }}
      >
        <iframe
          ref={iframeRef}
          title='mail-viewer'
          srcDoc={wrappedHtml}
          sandbox='allow-same-origin allow-popups allow-popups-to-escape-sandbox'
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
          }}
        />
      </Box>

      {iframeHeight > collapsedHeight && (
        <Grid container justifyContent="end">
        <Button
          variant='text'
          sx={{ mt: 0.5, textTransform: 'none', p: 0, color: 'primary.contrastText' }}
          onClick={(e) => {
            e.stopPropagation();          // <- viktig
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? '...se mindre' : '...se mer'}
        </Button>
        </Grid>
      )}
    </Box>
  );
};
